import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  BookOpen,
  GraduationCap,
  Check,
  Search,
  ChevronRight,
  Loader2,
  Users,
  User,
  UserCheck,
  X,
  CalendarIcon,
  FileText,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useCreateAssignment } from "@/hooks/useCreateAssignment";
import type { AssignmentType, UserSelectionMode } from "@/types/assignments";

interface CreateAssignmentDialogProps {
  onSuccess?: () => void;
}

const STEPS = [
  { number: 1, label: "Type" },
  { number: 2, label: "Item" },
  { number: 3, label: "Users" },
  { number: 4, label: "Options" },
  { number: 5, label: "Confirm" },
];

export function CreateAssignmentDialog({ onSuccess }: CreateAssignmentDialogProps) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [selectedType, setSelectedType] = useState<AssignmentType | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState<UserSelectionMode>("individual");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [required, setRequired] = useState(true);
  const [notes, setNotes] = useState("");

  // Search states
  const [itemSearch, setItemSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // Mutation hook
  const createAssignment = useCreateAssignment();

  // Fetch curricula
  const { data: curricula, isLoading: curriculaLoading } = useQuery({
    queryKey: ["curricula-for-assignment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curricula")
        .select("id, title, description, is_published")
        .order("title");

      if (error) throw error;
      return data;
    },
    enabled: open && selectedType === "curriculum",
  });

  // Fetch courses
  const { data: courses, isLoading: coursesLoading } = useQuery({
    queryKey: ["courses-for-assignment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("id, title, description, is_published")
        .order("title");

      if (error) throw error;
      return data;
    },
    enabled: open && selectedType === "course",
  });

  // Fetch all users for individual selection
  const { data: allUsers, isLoading: usersLoading } = useQuery({
    queryKey: ["users-for-assignment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role")
        .order("full_name");

      if (error) throw error;
      return data;
    },
    enabled: open && currentStep >= 3,
  });

  // Fetch role-based user counts
  const { data: roleCounts } = useQuery({
    queryKey: ["user-role-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("role");

      if (error) throw error;

      const counts = {
        mentor: 0,
        entrepreneur: 0,
        all: 0,
      };

      data?.forEach((user) => {
        counts.all++;
        if (user.role === "mentor") counts.mentor++;
        if (user.role === "entrepreneur") counts.entrepreneur++;
      });

      return counts;
    },
    enabled: open && currentStep >= 3,
  });

  // Filter items based on search
  const filteredItems = useMemo(() => {
    const items = selectedType === "curriculum" ? curricula : courses;
    if (!items) return [];

    if (!itemSearch.trim()) return items;

    const search = itemSearch.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search)
    );
  }, [selectedType, curricula, courses, itemSearch]);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    const items = selectedType === "curriculum" ? curricula : courses;
    return items?.find((item) => item.id === selectedItemId) || null;
  }, [selectedType, selectedItemId, curricula, courses]);

  const isItemsLoading = selectedType === "curriculum" ? curriculaLoading : coursesLoading;

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];

    if (!userSearch.trim()) return allUsers;

    const search = userSearch.toLowerCase();
    return allUsers.filter((user) =>
      user.full_name?.toLowerCase().includes(search)
    );
  }, [allUsers, userSearch]);

  // Get selected users details
  const selectedUsersDetails = useMemo(() => {
    if (!allUsers) return [];
    return allUsers.filter((user) => selectedUserIds.includes(user.id));
  }, [allUsers, selectedUserIds]);

  // Get user count for summary
  const getUserCountForSummary = () => {
    if (selectionMode === "individual") {
      return selectedUserIds.length;
    }
    if (selectionMode === "all_mentors") {
      return roleCounts?.mentor || 0;
    }
    if (selectionMode === "all_entrepreneurs") {
      return roleCounts?.entrepreneur || 0;
    }
    return roleCounts?.all || 0;
  };

  // Get target description for summary
  const getTargetDescription = () => {
    if (selectionMode === "individual") {
      return `${selectedUserIds.length} selected user${selectedUserIds.length !== 1 ? "s" : ""}`;
    }
    if (selectionMode === "all_mentors") {
      return `All Mentors (${roleCounts?.mentor || 0} users)`;
    }
    if (selectionMode === "all_entrepreneurs") {
      return `All Entrepreneurs (${roleCounts?.entrepreneur || 0} users)`;
    }
    return `All Users (${roleCounts?.all || 0} users)`;
  };

  // Reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (isSubmitting) return; // Prevent closing during submission
    setOpen(newOpen);
    if (!newOpen) {
      // Reset all state
      setCurrentStep(1);
      setSelectedType(null);
      setSelectedItemId(null);
      setSelectedUserIds([]);
      setSelectionMode("individual");
      setDueDate(undefined);
      setRequired(true);
      setNotes("");
      setItemSearch("");
      setUserSearch("");
      setIsSubmitting(false);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Remove user from selection
  const removeUserFromSelection = (userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  // Handle selection mode change
  const handleSelectionModeChange = (mode: UserSelectionMode) => {
    setSelectionMode(mode);
    // Clear individual selections when switching to role-based mode
    if (mode !== "individual") {
      setSelectedUserIds([]);
    }
  };

  // Resolve user IDs based on selection mode
  const resolveUserIds = async (): Promise<string[]> => {
    if (selectionMode === "individual") {
      return selectedUserIds;
    }

    // Fetch user IDs based on role
    let query = supabase.from("profiles").select("id");

    if (selectionMode === "all_mentors") {
      query = query.eq("role", "mentor");
    } else if (selectionMode === "all_entrepreneurs") {
      query = query.eq("role", "entrepreneur");
    }
    // all_users: no filter needed

    const { data, error } = await query;

    if (error) {
      throw new Error("Failed to fetch users: " + error.message);
    }

    return data?.map((u) => u.id) || [];
  };

  // Handle submission
  const handleSubmit = async () => {
    if (!selectedType || !selectedItemId) return;

    setIsSubmitting(true);

    try {
      // Resolve user IDs
      const userIds = await resolveUserIds();

      if (userIds.length === 0) {
        toast.error("No users to assign to");
        setIsSubmitting(false);
        return;
      }

      // Create assignment
      const result = await createAssignment.mutateAsync({
        type: selectedType,
        item_id: selectedItemId,
        user_ids: userIds,
        due_date: dueDate ? dueDate.toISOString() : undefined,
        required,
        notes: notes.trim() || undefined,
      });

      // Show success message
      const itemName = selectedItem?.title || "item";
      if (result.successCount > 0) {
        toast.success(
          `Successfully assigned "${itemName}" to ${result.successCount} user${result.successCount !== 1 ? "s" : ""}`,
          {
            description:
              result.skippedCount > 0
                ? `${result.skippedCount} user${result.skippedCount !== 1 ? "s" : ""} already had this assignment`
                : undefined,
          }
        );
      } else if (result.skippedCount > 0) {
        toast.info(`All ${result.skippedCount} users already have this assignment`);
      }

      // Close dialog and trigger callback
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create assignment:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to create assignment. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return selectedType !== null;
      case 2:
        return selectedItemId !== null;
      case 3:
        if (selectionMode === "individual") {
          return selectedUserIds.length > 0;
        }
        return true; // Role-based selection is valid if a mode is selected
      case 4:
        return true; // Options are optional
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 5 && canGoNext()) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 5) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <nav 
      className="flex items-center justify-center gap-1 mb-6" 
      aria-label="Assignment creation steps"
    >
      {STEPS.map((step, index) => (
        <div key={step.number} className="flex items-center">
          <div
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300",
              currentStep === step.number
                ? "bg-primary text-primary-foreground scale-110 shadow-md"
                : currentStep > step.number
                ? "bg-primary/20 text-primary"
                : "bg-muted text-muted-foreground"
            )}
            aria-current={currentStep === step.number ? "step" : undefined}
            aria-label={`Step ${step.number}: ${step.label}${currentStep > step.number ? " (completed)" : ""}`}
          >
            {currentStep > step.number ? (
              <Check className="h-4 w-4" />
            ) : (
              step.number
            )}
          </div>
          <span
            className={cn(
              "ml-2 text-sm hidden sm:inline transition-colors",
              currentStep === step.number
                ? "text-foreground font-medium"
                : "text-muted-foreground"
            )}
          >
            {step.label}
          </span>
          {index < STEPS.length - 1 && (
            <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
          )}
        </div>
      ))}
    </nav>
  );

  // Step 1: Select Type
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">What would you like to assign?</h3>
        <p className="text-sm text-muted-foreground">
          Choose whether to assign a curriculum (collection of courses) or a single course.
        </p>
      </div>

      <RadioGroup
        value={selectedType || ""}
        onValueChange={(value) => {
          setSelectedType(value as AssignmentType);
          setSelectedItemId(null); // Reset item when type changes
          setItemSearch("");
        }}
        className="grid gap-4"
      >
        <Label
          htmlFor="type-curriculum"
          className={cn(
            "flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors",
            selectedType === "curriculum"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="curriculum" id="type-curriculum" className="mt-1" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-600" />
              <span className="font-medium">Curriculum</span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Collection
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              A structured learning path containing multiple courses. Best for comprehensive training programs.
            </p>
          </div>
        </Label>

        <Label
          htmlFor="type-course"
          className={cn(
            "flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors",
            selectedType === "course"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="course" id="type-course" className="mt-1" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-green-600" />
              <span className="font-medium">Course</span>
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                Single
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              A single course with modules and lessons. Best for targeted training on specific topics.
            </p>
          </div>
        </Label>
      </RadioGroup>
    </div>
  );

  // Step 2: Select Item
  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium mb-2">
          Select a {selectedType === "curriculum" ? "Curriculum" : "Course"}
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the {selectedType === "curriculum" ? "curriculum" : "course"} you want to assign.
        </p>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={`Search ${selectedType === "curriculum" ? "curricula" : "courses"}...`}
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Items List */}
      <ScrollArea className="h-[280px] border rounded-lg">
        {isItemsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-8 w-8 mb-2" />
            <p className="text-sm">
              {itemSearch
                ? `No ${selectedType === "curriculum" ? "curricula" : "courses"} found`
                : `No ${selectedType === "curriculum" ? "curricula" : "courses"} available`}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItemId(item.id)}
                className={cn(
                  "w-full text-left p-3 rounded-lg transition-colors",
                  selectedItemId === item.id
                    ? "bg-primary/10 border-2 border-primary"
                    : "hover:bg-muted border-2 border-transparent"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {selectedType === "curriculum" ? (
                        <GraduationCap className="h-4 w-4 text-blue-600 shrink-0" />
                      ) : (
                        <BookOpen className="h-4 w-4 text-green-600 shrink-0" />
                      )}
                      <span className="font-medium truncate">{item.title}</span>
                      {!item.is_published && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          Draft
                        </Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                  {selectedItemId === item.id && (
                    <Check className="h-5 w-5 text-primary shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Selected Item Summary */}
      {selectedItem && (
        <div className="p-3 rounded-lg bg-muted/50 border">
          <p className="text-sm text-muted-foreground">Selected:</p>
          <p className="font-medium">{selectedItem.title}</p>
        </div>
      )}
    </div>
  );

  // Step 3: Select Users
  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium mb-2">Who should receive this assignment?</h3>
        <p className="text-sm text-muted-foreground">
          Select individual users or assign to all users with a specific role.
        </p>
      </div>

      {/* Selection Mode */}
      <RadioGroup
        value={selectionMode}
        onValueChange={(value) => handleSelectionModeChange(value as UserSelectionMode)}
        className="grid gap-2"
      >
        <Label
          htmlFor="mode-individual"
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
            selectionMode === "individual"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="individual" id="mode-individual" />
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Individual Users</span>
        </Label>

        <Label
          htmlFor="mode-all_mentors"
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
            selectionMode === "all_mentors"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="all_mentors" id="mode-all_mentors" />
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">All Mentors</span>
          {roleCounts && (
            <Badge variant="secondary" className="ml-auto">
              {roleCounts.mentor} users
            </Badge>
          )}
        </Label>

        <Label
          htmlFor="mode-all_entrepreneurs"
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
            selectionMode === "all_entrepreneurs"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="all_entrepreneurs" id="mode-all_entrepreneurs" />
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">All Entrepreneurs</span>
          {roleCounts && (
            <Badge variant="secondary" className="ml-auto">
              {roleCounts.entrepreneur} users
            </Badge>
          )}
        </Label>

        <Label
          htmlFor="mode-all_users"
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
            selectionMode === "all_users"
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <RadioGroupItem value="all_users" id="mode-all_users" />
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">All Users</span>
          {roleCounts && (
            <Badge variant="secondary" className="ml-auto">
              {roleCounts.all} users
            </Badge>
          )}
        </Label>
      </RadioGroup>

      {/* Individual User Selection */}
      {selectionMode === "individual" && (
        <div className="space-y-3 mt-4">
          {/* Selected Users Pills */}
          {selectedUserIds.length > 0 && (
            <div className="flex flex-wrap gap-2 p-2 rounded-lg bg-muted/50 border">
              {selectedUsersDetails.slice(0, 5).map((user) => (
                <Badge
                  key={user.id}
                  variant="secondary"
                  className="gap-1 pl-1 pr-2"
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={user.avatar_url || undefined} />
                    <AvatarFallback className="text-[10px]">
                      {user.full_name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="max-w-[100px] truncate">{user.full_name}</span>
                  <button
                    onClick={() => removeUserFromSelection(user.id)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedUserIds.length > 5 && (
                <Badge variant="outline">
                  +{selectedUserIds.length - 5} more
                </Badge>
              )}
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search users by name..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Users List */}
          <ScrollArea className="h-[180px] border rounded-lg">
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mb-2" />
                <p className="text-sm">
                  {userSearch ? "No users found" : "No users available"}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredUsers.map((user) => {
                  const isSelected = selectedUserIds.includes(user.id);
                  return (
                    <button
                      key={user.id}
                      onClick={() => toggleUserSelection(user.id)}
                      className={cn(
                        "w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left",
                        isSelected
                          ? "bg-primary/10"
                          : "hover:bg-muted"
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleUserSelection(user.id)}
                        className="pointer-events-none"
                      />
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar_url || undefined} />
                        <AvatarFallback>
                          {user.full_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {user.full_name || "Unnamed User"}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {user.role}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Selected Count */}
          <div className="text-sm text-muted-foreground text-center">
            {selectedUserIds.length === 0 ? (
              "No users selected"
            ) : (
              <span className="font-medium text-foreground">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? "s" : ""} selected
              </span>
            )}
          </div>
        </div>
      )}

      {/* Role-based Selection Info */}
      {selectionMode !== "individual" && (
        <div className="p-4 rounded-lg bg-muted/50 border mt-4">
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span>
              This will assign to{" "}
              <span className="font-medium text-foreground">
                {selectionMode === "all_mentors" && `${roleCounts?.mentor || 0} mentors`}
                {selectionMode === "all_entrepreneurs" && `${roleCounts?.entrepreneur || 0} entrepreneurs`}
                {selectionMode === "all_users" && `${roleCounts?.all || 0} users`}
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Step 4: Options
  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Assignment Options</h3>
        <p className="text-sm text-muted-foreground">
          Configure optional settings for this assignment.
        </p>
      </div>

      {/* Due Date */}
      <div className="space-y-2">
        <Label htmlFor="due-date">Due Date (optional)</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="due-date"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !dueDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dueDate ? format(dueDate, "PPP") : "No due date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dueDate}
              onSelect={setDueDate}
              disabled={(date) => date < new Date()}
              initialFocus
              className="p-3 pointer-events-auto"
            />
            {dueDate && (
              <div className="p-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setDueDate(undefined)}
                >
                  Clear date
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground">
          Set a deadline for users to complete this assignment.
        </p>
      </div>

      {/* Required */}
      <div className="flex items-start gap-3 p-4 rounded-lg border">
        <Checkbox
          id="required"
          checked={required}
          onCheckedChange={(checked) => setRequired(checked === true)}
          className="mt-0.5"
        />
        <div className="flex-1">
          <Label htmlFor="required" className="font-medium cursor-pointer">
            Mark as required
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            Required assignments will be highlighted for users and may affect their progress tracking.
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Notes (optional)</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this assignment..."
          className="min-h-[100px] resize-none"
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground text-right">
          {notes.length}/500 characters
        </p>
      </div>
    </div>
  );

  // Step 5: Confirmation
  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Review & Confirm</h3>
        <p className="text-sm text-muted-foreground">
          Please review the assignment details before creating.
        </p>
      </div>

      <div className="rounded-lg border overflow-hidden">
        {/* Header */}
        <div className="bg-muted/50 p-4 border-b">
          <p className="text-sm text-muted-foreground">You're about to assign:</p>
          <div className="flex items-center gap-2 mt-1">
            {selectedType === "curriculum" ? (
              <GraduationCap className="h-5 w-5 text-blue-600" />
            ) : (
              <BookOpen className="h-5 w-5 text-green-600" />
            )}
            <span className="font-semibold text-lg">{selectedItem?.title}</span>
          </div>
        </div>

        {/* Details */}
        <div className="divide-y">
          {/* Target Users */}
          <div className="flex items-start gap-3 p-4">
            <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Assign To</p>
              <p className="text-sm text-muted-foreground">{getTargetDescription()}</p>
            </div>
          </div>

          {/* Due Date */}
          <div className="flex items-start gap-3 p-4">
            <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Due Date</p>
              <p className="text-sm text-muted-foreground">
                {dueDate ? format(dueDate, "PPPP") : "No due date"}
              </p>
            </div>
          </div>

          {/* Required */}
          <div className="flex items-start gap-3 p-4">
            {required ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
            )}
            <div>
              <p className="text-sm font-medium">Required</p>
              <p className="text-sm text-muted-foreground">
                {required ? "Yes - this is a required assignment" : "No - this is optional"}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="flex items-start gap-3 p-4">
            <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Notes</p>
              <p className="text-sm text-muted-foreground break-words">
                {notes.trim() || "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning for bulk assignments */}
      {getUserCountForSummary() > 10 && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-800 dark:text-amber-200">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Bulk Assignment</p>
            <p className="text-sm opacity-90">
              This will create {getUserCountForSummary()} assignments. This action cannot be undone.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button aria-label="Create new assignment">
          <Plus className="h-4 w-4 mr-2" />
          Create Assignment
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col"
        aria-describedby="dialog-description"
      >
        {/* Loading Overlay */}
        {isSubmitting && (
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center animate-fade-in"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-sm font-medium">Creating assignments...</p>
            <p className="text-xs text-muted-foreground mt-1">
              Please wait while we assign to {getUserCountForSummary()} user{getUserCountForSummary() !== 1 ? "s" : ""}
            </p>
          </div>
        )}

        <DialogHeader>
          <DialogTitle>Create Assignment</DialogTitle>
          <p id="dialog-description" className="sr-only">
            A multi-step form to create learning assignments for users
          </p>
        </DialogHeader>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Step Content with animation */}
        <div 
          className="flex-1 overflow-y-auto py-2 animate-fade-in"
          key={currentStep} // Force re-render for animation
        >
          {renderCurrentStep()}
        </div>

        {/* Validation message for Step 3 */}
        {currentStep === 3 && selectionMode === "individual" && selectedUserIds.length === 0 && (
          <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2 px-1" role="alert">
            <AlertCircle className="h-4 w-4" />
            Select at least one user to continue
          </p>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-4 border-t mt-4">
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            aria-label="Cancel and close dialog"
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button 
                variant="outline" 
                onClick={handleBack} 
                disabled={isSubmitting}
                aria-label="Go to previous step"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canGoNext() || isSubmitting}
              aria-label={currentStep === 5 ? "Confirm and create assignments" : "Go to next step"}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : currentStep === 5 ? (
                "Confirm & Create"
              ) : (
                "Next"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
